<template>
  <v-col cols="12">
    <v-card class="elevation-4 mb-4">
      <!-- SELECT TIMEFRAME -->
      <v-row class="fill-height mb-3" align="center" justify="space-around">
        <v-btn-toggle v-model="selectedChartTimeframe" dark>
          <template v-for="timeframe in chartTimeframes">
            <v-btn :value="timeframe" :key="timeframe">{{ timeframe | toDays }}</v-btn>
          </template>
        </v-btn-toggle>
      </v-row>

      <!-- CHART -->
      <div :style="ui.chartMaxHeight">
        <canvas :id="chartName"></canvas>
      </div>

      <!-- TITLE OF Chart -->
      <v-card-title primary-title>
        <div>
          <h3 class="text-h5 mb0">Email Activity by Dealer</h3>
        </div>
      </v-card-title>
    </v-card>
  </v-col>
</template>

<script>
import 'chartjs-plugin-colorschemes'
import Chart from 'chart.js'
import { mapState } from 'vuex'
import { StatsApi } from '@/services'
import * as R from 'ramda'
import { MAIL_EVENT_NAMES } from '@/constants'
import { CHART_COLORS } from '@/components/charts'

export default {
  name: 'activity-by-dealer',
  data: function () {
    return {
      ui: {
        chartMaxHeight: 'max-height: 500px'
      },
      chartName: 'activityByDealer',
      chart: null,
      chartTimeframes: [1, 7, 30, 60, 90],
      selectedChartTimeframe: 7,
      displayCountFields: MAIL_EVENT_NAMES.map((field) => field + '_count'),
      dealerStats: [] // data from api
    }
  },
  computed: {
    ...mapState('DealerStore', ['dealers']),
    ...mapState({
      accountKey: state =>
        R.path(['attributes', 'custom:account_key'], state.cognito.user)
    }),
    xLabels () {
      return R.uniq(R.pluck('sender_id', this.dealerStats)).sort()
    },
    countFields () {
      return Object.keys((this.dealerStats[0] || {}))
        .filter((field) => field.endsWith('_count'))
        .filter((field) => this.displayCountFields.includes(field))
        .sort()
    },
    datasets () {
      const countByDealer = {}
      this.countFields.forEach((countKey) => {
        this.xLabels.forEach((dealerKey) => {
          countByDealer[countKey] = countByDealer[countKey] || []
          // could be optomized to groupby sender_id
          let record = this.dealerStats.find((statData) => statData.sender_id === dealerKey)
          countByDealer[countKey].push(record[countKey])
        })
      })
      return Object.entries(countByDealer).map(([countKey, counts]) => {
        return {
          label: countKey.replace(/_count/, ''),
          data: counts
        }
      })
    },
    chartData () {
      return {
        type: 'bar',
        data: {
          labels: this.xLabels,
          datasets: this.datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [
              { ticks: { min: 0 } }
            ],
            xAxes: [
              {
                ticks: {
                  callback: function (value, index, values) {
                    return this.lookupSenderName(value)
                  }.bind(this)
                }
              }
            ]
          },
          tooltips: {
            callbacks: {
              title: function (tooltipItems, data) {
                const xlabel = tooltipItems[0].xLabel
                // x label is either a short_code or dms_key
                const dealer = this.dealers.find((d) => {
                  return d.short_code === xlabel || d.dms_key === xlabel
                })

                if (dealer) {
                  return dealer.name
                } else {
                  return xlabel
                }
              }.bind(this)
            }
          },
          plugins: {
            colorschemes: {
              scheme: CHART_COLORS
            },
            datalabels: {
              display: false // this was the trick to turning off point labels - have to override plugin
            }
          }
        }
      }
    }
  },
  watch: {
    selectedChartTimeframe: {
      immediate: false,
      handler (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.$logger.trace('Loading of daily stats...')
          this.loadDealerStats()
        } else {
          this.$logger.trace('Skipping refresh of daily stats...')
        }
      }
    },
    chartData: {
      immediate: false,
      handler (newVal, oldVal) {
        this.$logger.trace('Refreshing Activity By Dealer chart')
        this.chart.type = newVal.type
        this.chart.data = newVal.data
        this.chart.options = newVal.options
        this.chart.update()
      }
    }
  },
  methods: {
    lookupSenderName (dmsKey) {
      const dealer = this.dealers.find((d) => d.dms_key === dmsKey)
      return (dealer || {}).short_code || dmsKey
    },
    findOrCreateChart (chartId, chartData) {
      if (this.chart) {
        return this.chart
      } else {
        const ctx = document.getElementById(chartId)
        const chart = new Chart(ctx, {
          type: chartData.type,
          data: chartData.data,
          options: chartData.options
        })
        this.chart = chart
        return chart
      }
    },
    loadDealerStats () {
      StatsApi.dealerStatsByPeriod({
        account_key: this.accountKey,
        past_n_days: this.selectedChartTimeframe
      })
        .then(stats => {
          this.dealerStats = stats
        })
        .catch(error => {
          this.$logger.error('Error loading activity by period from API', error.message)
          this.$flash({
            showClose: true,
            message: `Error loading activity by period from API: ${error.message}`,
            type: 'mini',
            duration: 3000
          })
        })
    },
    refreshChart () {
      this.findOrCreateChart(this.chartName, this.chartData)
      this.loadDealerStats()
    }
  },
  mounted: function () {
    this.refreshChart()
  }
}
</script>

<style>
</style>
